<script setup lang="ts">
import enPickerI18n from 'emoji-picker-element/i18n/en'
import esPickerI18n from 'emoji-picker-element/i18n/es'
import frPickerI18n from 'emoji-picker-element/i18n/fr'
import itPickerI18n from 'emoji-picker-element/i18n/it'
import { MISSION_EMOJI_CATEGORIES } from './missionEmojis'
import { MISSION_HEROICONS } from './missionIcons'
import { ChevronDown } from 'lucide-vue-next'

import type { TabKey } from '@/components/base/BaseTab.vue'

const { t, locale } = useI18n()

const PICKER_I18N = { en: enPickerI18n, es: esPickerI18n, fr: frPickerI18n, it: itPickerI18n } as const

const pickerLocale = computed<keyof typeof PICKER_I18N>(() =>
  locale.value in PICKER_I18N ? (locale.value as keyof typeof PICKER_I18N) : 'en'
)
const pickerI18n = computed(() => PICKER_I18N[pickerLocale.value])
const pickerDataSource = computed(
  () => `https://cdn.jsdelivr.net/npm/emoji-picker-element-data@^1/${pickerLocale.value}/emojibase/data.json`
)

const iconType = defineModel<'heroicon' | 'emoji'>('iconType', { required: true })
const iconValue = defineModel<string>('iconValue', { required: true })

const isOpen = ref(false)
const currentPanel = ref<string>(iconType.value)

const panels = computed<TabKey[]>(() => [
  { value: 'emoji', name: t('pages.loyaltySupports.webapp.missions.iconEmoji') },
  { value: 'heroicon', name: t('pages.loyaltySupports.webapp.missions.iconLibrary') }
])

const currentHeroicon = computed(() =>
  iconType.value === 'heroicon' ? (MISSION_HEROICONS[iconValue.value] ?? MISSION_HEROICONS.SparklesIcon) : null
)

const showAllEmojis = ref(false)
const emojiPickerLoaded = ref(false)

const loadEmojiPicker = async () => {
  showAllEmojis.value = true

  if (emojiPickerLoaded.value) return

  const module = (await import('emoji-picker-element')) as unknown as Record<string, never>

  if (!customElements.get('emoji-picker')) {
    customElements.define('emoji-picker', module.default ?? module)
  }

  emojiPickerLoaded.value = true
}

const onPickerEmoji = (event: Event) => {
  const detail = (event as CustomEvent<{ unicode?: string }>).detail
  if (!detail?.unicode) return

  select('emoji', detail.unicode)
}

const select = (type: 'heroicon' | 'emoji', value: string) => {
  iconType.value = type
  iconValue.value = value
  isOpen.value = false
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <Label>{{ t('pages.loyaltySupports.webapp.missions.icon') }}</Label>

    <Popover v-model:open="isOpen">
      <PopoverTrigger as-child>
        <button
          type="button"
          class="border-input flex h-10 w-full items-center gap-2 rounded-control border bg-transparent px-3 text-sm"
        >
          <component :is="currentHeroicon" v-if="currentHeroicon" class="size-5 text-foreground" aria-hidden="true" />
          <span v-else class="text-lg leading-none" aria-hidden="true">{{ iconValue }}</span>
          <span class="text-foreground-secondary">{{ t('pages.loyaltySupports.webapp.missions.changeIcon') }}</span>
          <ChevronDown class="ml-auto size-4 text-muted-foreground" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" class="w-[360px] p-3">
        <BaseTab v-model="currentPanel" :tabs="panels" disable-router />

        <div v-if="currentPanel === 'emoji' && showAllEmojis" class="mt-3">
          <button type="button" class="mb-2 text-xs text-muted-foreground underline" @click="showAllEmojis = false">
            {{ t('pages.loyaltySupports.webapp.missions.emojiSuggestions') }}
          </button>
          <emoji-picker
            v-if="emojiPickerLoaded"
            class="light"
            :i18n="pickerI18n"
            :locale="pickerLocale"
            :data-source="pickerDataSource"
            @emoji-click="onPickerEmoji"
          />
        </div>

        <div v-else-if="currentPanel === 'emoji'" class="mt-3 max-h-[260px] overflow-y-auto pr-1">
          <div v-for="category in MISSION_EMOJI_CATEGORIES" :key="category.key" class="mb-3">
            <p class="mb-1 text-xs font-medium text-muted-foreground">
              {{ t(`pages.loyaltySupports.webapp.missions.emojiCategories.${category.key}`) }}
            </p>
            <div class="grid grid-cols-8 gap-1">
              <button
                v-for="emoji in category.emojis"
                :key="emoji"
                type="button"
                class="flex size-8 items-center justify-center rounded-md text-lg hover:bg-muted"
                :class="iconType === 'emoji' && iconValue === emoji ? 'bg-muted ring-1 ring-border' : ''"
                @click="select('emoji', emoji)"
              >
                {{ emoji }}
              </button>
            </div>
          </div>

          <button type="button" class="mt-1 text-xs text-muted-foreground underline" @click="loadEmojiPicker">
            {{ t('pages.loyaltySupports.webapp.missions.emojiShowAll') }}
          </button>
        </div>

        <div v-else class="mt-3 grid max-h-[260px] grid-cols-8 gap-1 overflow-y-auto pr-1">
          <button
            v-for="(component, name) in MISSION_HEROICONS"
            :key="name"
            type="button"
            class="flex size-8 items-center justify-center rounded-md hover:bg-muted"
            :class="iconType === 'heroicon' && iconValue === name ? 'bg-muted ring-1 ring-border' : ''"
            :aria-label="name"
            @click="select('heroicon', name)"
          >
            <component :is="component" class="size-5 text-foreground" aria-hidden="true" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>
